// ExportRenewalModal.jsx
import React, { useState, useMemo } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FiDownload, FiRefreshCw } from "react-icons/fi";
import axios from "axios";
import { PDFDocument } from "pdf-lib";

const API_BASE = "https://rentdoc-a0d223e1499e.herokuapp.com/api";
const LOCAL_PDF_PATH = "/N1.pdf";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function currentYear() {
  return new Date().getFullYear();
}
function currentMonth() {
  return String(new Date().getMonth() + 1).padStart(2, "0");
}
function lastDayOfMonth(year, month) {
  const d = new Date(Number(year), Number(month), 0);
  return String(d.getDate()).padStart(2, "0");
}
function buildYearOptions() {
  const cy = currentYear();
  const years = [];
  for (let y = cy - 3; y <= cy + 2; y++) years.push(y);
  return years;
}
function formatMonthDisplay(year, month) {
  if (!year || !month) return "—";
  const m = MONTHS.find((mo) => mo.value === month);
  return `${m?.label || month} ${year}`;
}

// ── Fill one N1 PDF template with a row's data ──────────────────
async function fillN1ForRow(data, n1TemplateBytes) {
  const pdfDoc = await PDFDocument.load(n1TemplateBytes, {
    ignoreEncryption: true,
  });
  const form = pdfDoc.getForm();

  const trySet = (fieldName, value) => {
    try {
      form.getTextField(fieldName).setText(value || "");
    } catch (e) {
      // field may not exist in this PDF version — skip silently
    }
  };

  const tryRadio = (fieldName, value) => {
    try {
      const field = form.getRadioGroup(fieldName);
      const options = field.getOptions();
      if (options.includes(value)) field.select(value);
    } catch (e) {}
  };

  const tryCheck = (fieldName) => {
    try {
      form.getCheckBox(fieldName).check();
    } catch (e) {}
  };

  // Tenant & Landlord
  trySet(
    "form1[0].#subform[0].Notice_Name_And_Address_1[0].To_TenantName[0]",
    data.tenantName + (data.tenantAddress ? "\n" + data.tenantAddress : ""),
  );
  trySet(
    "form1[0].#subform[0].Notice_Name_And_Address_1[0].From_LandlordName[0]",
    data.landlordName,
  );
  trySet(
    "form1[0].#subform[0].Notice_Name_And_Address_1[0].RentUnitAddress[0]",
    data.rentalUnitAddress,
  );

  // Effective date
  trySet(
    "form1[0].#subform[0].StartDate[0]",
    `${data.effectiveDay}/${data.effectiveMonth}/${data.effectiveYear}`,
  );

  // Rent & increase
  trySet("form1[0].#subform[0].RentIncAmount1[0]", data.newRent);
  trySet("form1[0].#subform[0].RentIncAmount2[0]", data.increaseAmount);
  trySet("form1[0].#subform[0].RentIncPercent[0]", data.increasePercent);

  // Period — monthly
  tryRadio("form1[0].#subform[0].period[0].PaymentPeriodM[0]", "1");
  tryRadio("form1[0].#subform[0].period[1].PaymentPeriodM[0]", "1");

  // Guideline
  if (data.guideline === "lessOrEqual") {
    tryRadio("form1[0].#subform[0].Check1[0]", "1");
  } else if (data.guideline === "moreApproved") {
    tryRadio("form1[0].#subform[0].Check2[0]", "1");
    tryCheck("form1[0].#subform[0].Check_2_1[0]");
  } else if (data.guideline === "moreApplied") {
    tryRadio("form1[0].#subform[0].Check2[0]", "1");
    tryCheck("form1[0].#subform[0].Check_2_2[0]");
  }

  // Signature section
  tryRadio(
    "form1[0].#subform[5].SelectSign[0]",
    data.signerType === "landlord" ? "1" : "2",
  );
  trySet(
    "form1[0].#subform[5].SignatureN1ToN3[0].SignName[0]",
    data.signerName,
  );
  trySet(
    "form1[0].#subform[5].SignatureN1ToN3[0].SignPhoneNum[0]",
    data.signerPhone,
  );
  trySet("form1[0].#subform[5].SignatureN1ToN3[0].SignDate[0]", data.signDate);

  // Representative info
  if (data.signerType === "representative") {
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentName[0]",
      data.repName,
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentLSUC[0]",
      data.repLSUC || "",
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentCompany[0]",
      data.repCompany,
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentAddress[0]",
      data.repAddress,
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentPhoneNum[0]",
      data.repPhone,
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentMunicipality[0]",
      data.repCity,
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentProvince[0]",
      data.repProvince,
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentPostCode[0]",
      data.repPostalCode,
    );
    trySet(
      "form1[0].#subform[5].Agent_Information_for_Notice[0].AgentFaxNum[0]",
      data.repFax || "",
    );
  }

  // Embed signature image
  const sigUrl =
    data.signerType === "landlord"
      ? data.landlordSigUrl
      : data.representativeSigUrl;

  if (sigUrl) {
    try {
      const sigResp = await fetch(sigUrl);
      const sigBytes = await sigResp.arrayBuffer();
      const isJpeg =
        sigUrl.toLowerCase().includes(".jpg") ||
        sigUrl.toLowerCase().includes("image/jpeg");
      const sigImage = isJpeg
        ? await pdfDoc.embedJpg(sigBytes)
        : await pdfDoc.embedPng(sigBytes);

      // Try form field first
      let placed = false;
      try {
        const sigField = form.getTextField(
          "form1[0].#subform[5].SignatureN1ToN3[0].Signature[0]",
        );
        sigField.setImage(sigImage);
        placed = true;
      } catch (e) {}

      if (!placed) {
        const pages = pdfDoc.getPages();
        const sigPage = pages[5] || pages[pages.length - 1];
        const dims = sigImage.scale(0.35);
        sigPage.drawImage(sigImage, {
          x: 80,
          y: 340,
          width: dims.width,
          height: dims.height,
        });
      }
    } catch (sigErr) {
      console.warn(
        `Could not embed signature for ${data.unitNumber}:`,
        sigErr.message,
      );
    }
  }

  return await pdfDoc.save();
}

// ── Merge array of Uint8Array PDF bytes into one PDF ────────────
async function mergePdfs(pdfBytesArray) {
  const merged = await PDFDocument.create();

  for (const bytes of pdfBytesArray) {
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const pageIndices = src.getPageIndices();
    const copied = await merged.copyPages(src, pageIndices);
    copied.forEach((page) => merged.addPage(page));
  }

  return await merged.save();
}

// ── Component ────────────────────────────────────────────────────
export default function ExportRenewalModal({ open, onClose }) {
  const yearOptions = useMemo(() => buildYearOptions(), []);

  const [fromYear, setFromYear] = useState(String(currentYear()));
  const [fromMonth, setFromMonth] = useState(currentMonth());
  const [toYear, setToYear] = useState(String(currentYear()));
  const [toMonth, setToMonth] = useState(currentMonth());
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);

  const fromDate = fromYear && fromMonth ? `${fromYear}-${fromMonth}-01` : "";
  const toDate =
    toYear && toMonth
      ? `${toYear}-${toMonth}-${lastDayOfMonth(toYear, toMonth)}`
      : "";
  const isInvalidRange = fromDate && toDate && fromDate > toDate;

  const handleExport = async () => {
    if (!fromDate || !toDate || isInvalidRange) return;
    setExporting(true);
    setProgress("Fetching renewal data...");
    setError(null);
    const token = localStorage.getItem("token");

    try {
      // 1. Fetch data from backend
      const res = await axios.get(
        `${API_BASE}/buildium/leases/export-renewals`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { from: fromDate, to: toDate },
        },
      );

      const { rows } = res.data;

      if (!rows || rows.length === 0) {
        setError("No renewals found for the selected period.");
        setExporting(false);
        setProgress("");
        return;
      }

      // 2. Load N1 template once
      setProgress("Loading N1 template...");
      const templateResp = await fetch(LOCAL_PDF_PATH);
      if (!templateResp.ok) throw new Error("Could not load N1 PDF template");
      const n1TemplateBytes = await templateResp.arrayBuffer();

      // 3. Fill one N1 per row
      const filledPdfs = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        setProgress(
          `Generating N1 ${i + 1} of ${rows.length}: ${row.propertyName} — Unit ${row.unitNumber || "?"}`,
        );
        const filled = await fillN1ForRow(row, n1TemplateBytes);
        filledPdfs.push(filled);
      }

      // 4. Merge all into one PDF
      setProgress(`Merging ${filledPdfs.length} N1 form(s)...`);
      const mergedBytes = await mergePdfs(filledPdfs);

      // 5. Download
      const blob = new Blob([mergedBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `N1_Renewals_${fromYear}-${fromMonth}_to_${toYear}-${toMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      console.error("Export failed:", err);
      setError(err.response?.data?.message || err.message || "Export failed.");
    } finally {
      setExporting(false);
      setProgress("");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/30"
        onClick={!exporting ? onClose : undefined}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-xl bg-white shadow-2xl z-10">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h3 className="text-base font-bold text-gray-900">
              Export N1 Renewal Forms
            </h3>
            {!exporting && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* From */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                From
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Month
                  </label>
                  <select
                    value={fromMonth}
                    onChange={(e) => setFromMonth(e.target.value)}
                    disabled={exporting}
                    className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Year
                  </label>
                  <select
                    value={fromYear}
                    onChange={(e) => setFromYear(e.target.value)}
                    disabled={exporting}
                    className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-xs text-gray-400 font-medium">TO</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* To */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                To
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Month
                  </label>
                  <select
                    value={toMonth}
                    onChange={(e) => setToMonth(e.target.value)}
                    disabled={exporting}
                    className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Year
                  </label>
                  <select
                    value={toYear}
                    onChange={(e) => setToYear(e.target.value)}
                    disabled={exporting}
                    className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {toDate && (
                <p className="text-xs text-gray-400 mt-1.5">
                  Ends on <strong className="text-gray-600">{toDate}</strong>{" "}
                  <span>(last day of month, auto)</span>
                </p>
              )}
            </div>

            {isInvalidRange ? (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                "From" month cannot be after "To" month.
              </div>
            ) : fromDate && toDate ? (
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
                Exporting N1 forms for renewals between{" "}
                <strong>{formatMonthDisplay(fromYear, fromMonth)}</strong> and{" "}
                <strong>{formatMonthDisplay(toYear, toMonth)}</strong>.
                <div className="text-xs text-blue-600 mt-1">
                  One filled N1 PDF per renewal, combined into a single
                  download.
                </div>
              </div>
            ) : null}

            {/* Progress indicator */}
            {exporting && progress && (
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600 flex items-center gap-2">
                <FiRefreshCw className="w-4 h-4 animate-spin flex-shrink-0 text-blue-500" />
                <span>{progress}</span>
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
            <button
              onClick={onClose}
              disabled={exporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !fromDate || !toDate || !!isInvalidRange}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition ${
                exporting || !fromDate || !toDate || isInvalidRange
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {exporting ? (
                <>
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                  Building PDF...
                </>
              ) : (
                <>
                  <FiDownload className="w-4 h-4" />
                  Download N1 Forms
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
